import { useMemo } from 'react';
import { PlugZap } from 'lucide-react';
import type { LiveSummary, Platform, ChatConnectionEvent } from '@multimod/shared';

type ChatConnReason = ChatConnectionEvent['reason'];
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { PlatformIcon } from '@/components/ui/platform-icon';
import { LivesList } from '@/features/live-discovery/LivesList';
import { LiveCard } from '@/features/live-discovery/LiveCard';
import { YouTubeLiveInput } from '@/features/live-discovery/YouTubeLiveInput';
import { ConnectYouTubeButton } from '@/features/auth/ConnectYouTubeButton';
import { useMe } from '@/features/auth/hooks';
import { connVisual } from '@/features/chat-feed/conn-visual';
import type { ChatFeedStore, ConnState } from '@/features/chat-feed/store';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selected: LiveSummary | null;
  onSelectPrimary: (live: LiveSummary) => void;
  attachedLives: LiveSummary[];
  onAddAttached: (live: LiveSummary) => void;
  // Store ativo — alimenta o badge de estado de conexão por plataforma. Null
  // quando nenhuma live está conectada (aí o badge não aparece).
  store?: ChatFeedStore | null;
}

// Card de plataforma com BORDA colorida PERMANENTE pelo estado de conexão —
// sempre visível, inclusive cinza no `idle` (não conectado). É o feedback que
// o mod pediu: dá pra ver de relance, sem hover, se a plataforma travou (azul),
// conectou (verde), está reconectando (amarelo) ou falhou (vermelho).
//
// Split em três: `PlatformCard` decide se assina o store (sem hook condicional);
// `PlatformCardLive` assina; `PlatformCardShell` pinta a borda + linha de status.
// Copy pt-BR por reason (o reason é enum técnico em inglês — não vai cru pra UI).
// Travado pelo ui-ux. Cada texto é autodescritivo, então o estado `failed` NÃO
// prefixa "Falha —" (evita "Falha — Falha inesperada"); a cor + ícone já dizem
// que é erro.
const REASON_COPY: Partial<Record<NonNullable<ChatConnReason>, string>> = {
  live_ended: 'Live encerrada',
  detached: 'Desconectado',
  auth: 'Sessão expirada',
  network: 'Sem conexão',
  rate_limit: 'Limite atingido',
  quota_exhausted: 'Cota esgotada',
  unknown: 'Falha inesperada',
};

function connLabel(state: ConnState, retryInMs?: number, reason?: ChatConnReason): string {
  // "Sem live conectada" (estado da LIVE) é distinto do "Não conectado" do
  // PlatformSectionHeader (estado do CANAL OAuth) — evita texto ambíguo duplicado.
  if (state === 'idle') return 'Sem live conectada';
  if (state === 'connected') return 'Conectado — recebendo mensagens';
  if (state === 'reconnecting') {
    return retryInMs ? `Reconectando em ${Math.ceil(retryInMs / 1000)}s…` : 'Reconectando…';
  }
  const copy = reason ? REASON_COPY[reason] : undefined;
  if (state === 'ended') return copy ?? 'Encerrado';
  if (state === 'failed') return copy ?? 'Falha ao conectar';
  return connVisual(state).label; // connecting → "Conectando…"
}

function PlatformCardShell({
  state,
  retryInMs,
  reason,
  ariaLabel,
  children,
}: {
  state: ConnState;
  retryInMs?: number;
  reason?: ChatConnReason;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const v = connVisual(state);
  const { Icon } = v;
  return (
    <section
      aria-label={ariaLabel}
      data-conn-state={state}
      className={`rounded-lg border p-3 transition-colors duration-base ${v.cardBorder}`}
    >
      <div
        role="status"
        aria-live="polite"
        className={`mb-3 flex items-center gap-1.5 text-xs font-medium ${v.textClass}`}
      >
        <Icon
          size={14}
          strokeWidth={1.75}
          aria-hidden="true"
          className={v.animate ? 'animate-spin shrink-0' : 'shrink-0'}
        />
        <span>{connLabel(state, retryInMs, reason)}</span>
      </div>
      {children}
    </section>
  );
}

function PlatformCardLive({
  store,
  platform,
  ariaLabel,
  children,
}: {
  store: ChatFeedStore;
  platform: Platform;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const conn = store((s) => s.connectionByPlatform[platform]);
  return (
    <PlatformCardShell
      state={conn?.state ?? 'idle'}
      {...(conn?.retryInMs !== undefined ? { retryInMs: conn.retryInMs } : {})}
      {...(conn?.reason !== undefined ? { reason: conn.reason } : {})}
      ariaLabel={ariaLabel}
    >
      {children}
    </PlatformCardShell>
  );
}

function PlatformCard({
  store,
  platform,
  ariaLabel,
  children,
}: {
  store: ChatFeedStore | null;
  platform: Platform;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  if (store) {
    return (
      <PlatformCardLive store={store} platform={platform} ariaLabel={ariaLabel}>
        {children}
      </PlatformCardLive>
    );
  }
  // Sem store (nenhuma live conectada) → idle, borda cinza permanente.
  return (
    <PlatformCardShell state="idle" ariaLabel={ariaLabel}>
      {children}
    </PlatformCardShell>
  );
}

// Header por plataforma: vocabulário "Canal conectado" (OAuth, persiste) vs
// "Live ativa" (sessão atual). Reforça que o canal é estável e a live é
// efêmera, evitando confusão (decisão roteiro Fase 1).
function PlatformSectionHeader({
  platform,
  connectedAs,
  activeLiveDisplayName,
}: {
  platform: Platform;
  connectedAs: string | null;
  activeLiveDisplayName: string | null;
}) {
  const color = platform === 'twitch' ? 'text-platform-twitch' : 'text-platform-youtube';
  return (
    <header className="flex items-start justify-between gap-3 mb-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <PlatformIcon platform={platform} size={14} className={color} />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-fg">
            {platform === 'twitch' ? 'Twitch' : 'YouTube'}
          </h3>
        </div>
        {connectedAs ? (
          <p className="text-2xs text-fg-muted mt-0.5 truncate">
            <span>Canal conectado: </span>
            <span className="text-fg font-medium">{connectedAs}</span>
            {activeLiveDisplayName && (
              <>
                <span aria-hidden="true"> · </span>
                <span>Live ativa: </span>
                <span className="text-fg font-medium">{activeLiveDisplayName}</span>
              </>
            )}
          </p>
        ) : (
          <p className="text-2xs text-fg-subtle italic mt-0.5">Não conectado</p>
        )}
      </div>
    </header>
  );
}

export function DiscoverySheet({
  open,
  onOpenChange,
  selected,
  onSelectPrimary,
  attachedLives,
  onAddAttached,
  store = null,
}: Props) {
  const me = useMe();
  const twitchConn = me.data?.connections?.twitch ?? null;
  const ytConn = me.data?.connections?.youtube ?? null;
  const ytConnected = Boolean(ytConn);
  const primaryIsYoutube = selected?.platform === 'youtube';
  const ytAttached = useMemo(
    () => attachedLives.filter((l) => l.platform === 'youtube'),
    [attachedLives],
  );

  const activeTwitchName = useMemo(() => {
    if (selected?.platform === 'twitch') return selected.displayName;
    return attachedLives.find((l) => l.platform === 'twitch')?.displayName ?? null;
  }, [selected, attachedLives]);
  const activeYouTubeName = useMemo(() => {
    if (selected?.platform === 'youtube') return selected.displayName;
    return ytAttached[0]?.displayName ?? null;
  }, [selected, ytAttached]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:max-w-md flex flex-col gap-4">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {/* #120: PlugZap (plug-zap) — coerente com o vocabulário de "anexar/conectar". */}
            <PlugZap size={16} strokeWidth={2} aria-hidden="true" />
            Conectar live
          </SheetTitle>
          <SheetDescription>
            Escolha a live primary; opcionalmente, anexe a contraparte na outra plataforma.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto flex flex-col gap-6 -mx-6 px-6">
          <PlatformCard store={store} platform="twitch" ariaLabel="Twitch — lives ao vivo">
            <PlatformSectionHeader
              platform="twitch"
              connectedAs={twitchConn?.displayName ?? null}
              activeLiveDisplayName={activeTwitchName}
            />
            <p className="text-2xs uppercase tracking-wider text-fg-subtle mb-2 mt-3">
              Ao vivo agora
            </p>
            <LivesList
              onSelect={onSelectPrimary}
              {...(primaryIsYoutube ? { onAttach: onAddAttached } : {})}
            />
          </PlatformCard>

          <PlatformCard store={store} platform="youtube" ariaLabel="YouTube — colar URL da live">
            <PlatformSectionHeader
              platform="youtube"
              connectedAs={ytConn?.displayName ?? null}
              activeLiveDisplayName={activeYouTubeName}
            />
            {!ytConnected ? (
              <ConnectYouTubeButton connected={false} />
            ) : (
              <div className="flex flex-col gap-3">
                <YouTubeLiveInput onResolved={onAddAttached} />
                {ytAttached.length > 0 && (
                  <>
                    <p className="text-2xs uppercase tracking-wider text-fg-subtle mt-2">
                      Live ativa
                    </p>
                    <ul className="flex flex-col gap-2">
                      {ytAttached.map((live) => (
                        <li key={live.liveChatId}>
                          <LiveCard live={live} onSelect={onSelectPrimary} />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </PlatformCard>
        </div>

        <SheetFooter className="items-center gap-2">
          {/* #120: trust strip — reduz ansiedade de "isso é permanente?". */}
          <p className="mr-auto text-2xs text-fg-subtle max-w-[200px] leading-snug">
            Anexar é instantâneo. Você pode trocar a primary depois.
          </p>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!selected}
            onClick={() => onOpenChange(false)}
          >
            Concluir
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
